/**
 * What a condition MEANS, in words the operator can check: the metric with
 * its formula and unit (as the day aggregate computes it), the operator
 * with its boundary spelled out, and the whole condition as one sentence.
 * The formulas mirror the backend's aggregate (counters summed, ratios
 * recomputed from the sums); two metrics cannot be derived for a day and
 * say so — a rule on them keeps the verdict inconclusive.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FLOW_METRIC_LABELS, FLOW_METRIC_MEANING } from '@trafficpulse/shared';

import { money } from '@/lib/reporting-currency';

import { GATE_METRIC_OPTIONS } from '@/features/drop-generator/components/editor/verdicts/condition/gate-metric-options';

import type { DraftVerdictRow } from '../../../../drop-generator.types';
import { isTagRow } from '../../../../drop-generator-rule.types';

/** Phase 39 — the Flow metrics a tracker source reads: rates in %, money in the reporting currency (US dollars by default, 28/10/2026). */
const FLOW_PERCENT = new Set(['roi', 'ctr', 'cr', 'cv', 'uctr', 'ucr']);
const FLOW_MONEY = new Set(['revenue', 'cost', 'profit', 'epv', 'epc', 'epl', 'uEPC', 'cpv', 'cpc', 'ecpa', 'ecpm', 'rpm']);

/** Metric id → what it is, how it is computed for the day. */
/** Phase 41 — the words for the SMSWiser and combined metrics; a tracker metric uses the list stats' own words (FLOW_METRIC_MEANING). */
export const METRIC_MEANING: Record<string, string> = {
  suspiciousClicks: 'tracker clicks flagged suspicious',
  suspiciousClicksRatio: 'suspicious clicks ÷ clicks, in %',
  uniqueClickRatio: 'unique clicks ÷ clicks, in %',
  smsSent: 'SMS sent',
  smsDelivered: 'SMS delivered',
  smsUndeliverable: 'SMS undeliverable',
  smsBotClicks: 'SMS link clicks flagged as bots',
  smsClicks: 'SMS link clicks',
  smsCtr: 'SMSWiser’s own link CTR, per drop — NOT available for a day added up: a rule on it stays inconclusive',
  deliveredRate: 'delivered ÷ sent, in %',
  undeliverableRate: 'undeliverable ÷ sent, in %',
  botRate: 'bot clicks ÷ SMS clicks, in %',
  realCtr: 'tracker unique clicks ÷ SMSWiser delivered, in %',
};

export const JOIN_MEANING = {
  AND: 'ALL — every condition must hold at once',
  OR: 'ANY — one holding condition is enough',
} as const;

const FORMAT = new Map<string, string>(GATE_METRIC_OPTIONS.map((o) => [o.value, o.format]));
const LABEL = new Map<string, string>(GATE_METRIC_OPTIONS.map((o) => [o.value, o.label]));

/** The threshold as the operator reads it: 0.85 → "85 %", 2.5 → "$2.50", -10000 → "-$10,000", 40 → "40 %". */
export function valueLabel(metric: string, value: number): string {
  if (!Number.isFinite(value)) return '—';
  const format = FORMAT.get(metric) ?? (FLOW_PERCENT.has(metric) ? 'percent' : FLOW_MONEY.has(metric) ? 'money' : 'int');
  if (format === 'ratio') return `${Math.round(value * 1000) / 10} %`;
  if (format === 'percent') return `${value} %`;
  if (format === 'money') return money(value);
  return value.toLocaleString('en-US');
}

/** The operator with its boundary spelled out. */
export function operatorMeaning(op: string, v: string): string {
  switch (op) {
    case '<':
      return `is strictly below ${v} — exactly ${v} does not count`;
    case '<=':
      return `is ${v} or below — exactly ${v} counts`;
    case '>':
      return `is strictly above ${v} — exactly ${v} does not count`;
    case '>=':
      return `is ${v} or above — exactly ${v} counts`;
    case '=':
      return `is exactly ${v}`;
    case '!=':
      return `is anything but exactly ${v}`;
    default:
      return `${op} ${v}`;
  }
}

/** "Delivered Rate (delivered ÷ sent, in %) is strictly below 85 % — exactly 85 % does not count."
 *  Phase 39 — `subject` names what a tag row judges ("any of today's drops", "the gateway"). */
export function conditionSentence(row: DraftVerdictRow, subject = 'The list of the day'): string {
  // Phase 38 — a tag row: the judged thing carries / does not carry a Tag Rules tag.
  if (isTagRow(row)) return `${subject} ${row.negate ? 'is not' : 'is'} tagged "${row.tag}" (a Tag Rules tag); one the tag cannot judge is unknown — unknown never acts.`;
  const label = LABEL.get(row.metric) ?? FLOW_METRIC_LABELS[row.metric] ?? row.metric;
  const meaning = METRIC_MEANING[row.metric] ?? FLOW_METRIC_MEANING[row.metric];
  return `${label}${meaning ? ` (${meaning})` : ''} ${operatorMeaning(row.op, valueLabel(row.metric, row.value))}.`;
}
